import { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ref, set, onValue, off } from 'firebase/database';
import { db } from '../firebaseConfig';
import Navbar from '../components/Navbar';
import { useLanguage } from '../LanguageContext';

export default function TrackingPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t } = useLanguage();
  const [user, setUser] = useState(null);
  const [isTracking, setIsTracking] = useState(false);
  const [myLocation, setMyLocation] = useState(null);
  const [otherLocation, setOtherLocation] = useState(null);
  const [speed, setSpeed] = useState(0);
  const watchIdRef = useRef(null);

  const tripId = searchParams.get('tripId') || searchParams.get('rideId') || 'demo';
  const otherName = searchParams.get('otherName') || 'Other person';
  const mode = searchParams.get('mode') || 'passenger';

  useEffect(() => {
    const savedUser = localStorage.getItem('guningo_user');
    if (!savedUser) {
      navigate('/');
      return;
    }
    setUser(JSON.parse(savedUser));
  }, [navigate]);

  useEffect(() => {
    const otherPath = 'liveTracking/' + tripId + '/' + (mode === 'passenger' ? 'driver' : 'passenger');
    const locRef = ref(db, otherPath);
    const unsubscribe = onValue(locRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        setOtherLocation({ latitude: data.latitude, longitude: data.longitude });
      }
    });
    return () => off(locRef);
  }, [tripId, mode]);

  const startTracking = () => {
    if (!navigator.geolocation) {
      alert('Location tracking is not supported by your browser.');
      return;
    }
    setIsTracking(true);
    watchIdRef.current = navigator.geolocation.watchPosition(
      function (pos) {
        const coords = { latitude: pos.coords.latitude, longitude: pos.coords.longitude };
        setMyLocation(coords);
        setSpeed(Math.round((pos.coords.speed || 0) * 3.6));
        const myPath = 'liveTracking/' + tripId + '/' + mode;
        set(ref(db, myPath), {
          latitude: coords.latitude,
          longitude: coords.longitude,
          name: user.name,
          updatedAt: Date.now(),
        });
      },
      function () {
        alert('Could not access your location. Please allow location permission.');
      },
      { enableHighAccuracy: true }
    );
  };

  const stopTracking = () => {
    if (watchIdRef.current) {
      navigator.geolocation.clearWatch(watchIdRef.current);
      watchIdRef.current = null;
    }
    setIsTracking(false);
  };

  useEffect(() => {
    return function () {
      if (watchIdRef.current) navigator.geolocation.clearWatch(watchIdRef.current);
    };
  }, []);

  if (!user) return null;

  let mapContent;
  if (myLocation) {
    const mapsUrl = 'https://www.google.com/maps?q=' + myLocation.latitude + ',' + myLocation.longitude;
    mapContent = (
      <div>
        <p style={styles.locLine}>Your location: {myLocation.latitude.toFixed(4)}, {myLocation.longitude.toFixed(4)}</p>
        {otherLocation ? (
          <p style={styles.locLine}>{otherName}'s location: {otherLocation.latitude.toFixed(4)}, {otherLocation.longitude.toFixed(4)}</p>
        ) : null}
        <a style={styles.mapLink} href={mapsUrl} target="_blank" rel="noopener noreferrer">View on Google Maps</a>
      </div>
    );
  } else {
    mapContent = <p style={{ color: '#9499b5', margin: 0 }}>Location not shared yet</p>;
  }

  return (
    <div style={styles.page}>
      <Navbar userName={user.name} userPhone={user.phone} />
      <div style={styles.container}>
        <h2 style={styles.pageTitle}>Live Tracking</h2>
        <p style={styles.pageSub}>Tracking with {otherName}</p>

        <div style={styles.mapCard}>
          {mapContent}
        </div>

        <div style={styles.statsRow}>
          <div style={styles.statBox}>
            <div style={styles.statVal}>{speed}</div>
            <div style={styles.statLbl}>km/h</div>
          </div>
        </div>

        <div style={styles.statusBar}>
          <span style={{ ...styles.statusDot, background: isTracking ? '#00c896' : '#f5a623' }}></span>
          <span style={styles.statusText}>{isTracking ? 'Sharing your live location' : 'Tracking not started'}</span>
        </div>

        {isTracking ? (
          <button style={styles.stopBtn} onClick={stopTracking}>Stop tracking</button>
        ) : (
          <button style={styles.startBtn} onClick={startTracking}>Start tracking</button>
        )}
      </div>
    </div>
  );
}

const styles = {
  page: { minHeight: '100vh', background: '#ffffff', fontFamily: 'Arial, sans-serif' },
  container: { maxWidth: 520, margin: '0 auto', padding: '30px 20px 40px' },
  pageTitle: { color: '#1a1a2e', margin: '0 0 4px' },
  pageSub: { color: '#5a5f7d', fontSize: 13, marginBottom: 20 },
  mapCard: { background: '#eaf5fc', border: '1px solid #d9edfa', borderRadius: 14, padding: 20, marginBottom: 14, minHeight: 100 },
  locLine: { fontSize: 12, color: '#1a1a2e', margin: '0 0 6px' },
  mapLink: { color: '#3a8fd0', fontWeight: 600, fontSize: 12 },
  statsRow: { display: 'flex', gap: 8, marginBottom: 14 },
  statBox: { flex: 1, border: '1px solid #eef1f8', borderRadius: 12, padding: 14, textAlign: 'center' },
  statVal: { fontSize: 20, fontWeight: 700, color: '#1a1a2e' },
  statLbl: { fontSize: 11, color: '#9499b5', marginTop: 2 },
  statusBar: { display: 'flex', alignItems: 'center', gap: 8, background: '#f7f9fc', borderRadius: 10, padding: 12, marginBottom: 14 },
  statusDot: { width: 8, height: 8, borderRadius: 4, display: 'inline-block' },
  statusText: { fontSize: 13, color: '#5a5f7d' },
  startBtn: { width: '100%', height: 50, background: '#3a8fd0', color: '#fff', border: 'none', borderRadius: 12, fontSize: 15, fontWeight: 700, cursor: 'pointer' },
  stopBtn: { width: '100%', height: 50, background: '#e94560', color: '#fff', border: 'none', borderRadius: 12, fontSize: 15, fontWeight: 700, cursor: 'pointer' },
};